import React from 'react';
import { useIntl } from 'react-intl';
import { useLocalizationContext } from '../LocalizationProvider';
import toDateTime from '../util/toDateTime';
import { getTimeZoneString } from './Timezone';
import { useEntityContext } from '../../../util/entity-provider/context';
import { isNil } from 'lodash';

const time = ({
  date,
  formatTime,
  formatMessage,
  timeZone,
  messages,
  hideTimeZone,
  userLocale,
  hour12 = undefined,
  useEntityTimeFormat = false,
  entityUse24HourTimeFormat,
}) => {
  if (!date) return '';

  let timeFormatToUse = hour12;
  if (useEntityTimeFormat && !isNil(entityUse24HourTimeFormat)) {
    timeFormatToUse = !entityUse24HourTimeFormat;
  }

  const options = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    hour12: timeFormatToUse,
  };

  const dateTime = toDateTime(date, timeZone);

  const timeZoneString = getTimeZoneString({ dateTime, timeZone, messages, userLocale, formatMessage });

  const formattedTime = formatTime(dateTime, options);

  return hideTimeZone ? formattedTime : `${formattedTime} (${timeZoneString})`;
};

export const Time = ({ date, hideTimeZone = false, testId, useEntityTimeFormat = false }) => {
  const { formatTime, formatMessage } = useIntl();
  const { timeZone, messages, userLocale } = useLocalizationContext();
  const { entitySetup } = useEntityContext();
  const entityUse24HourTimeFormat = entitySetup?.dateTimeSettings?.use24HourTimeFormat;

  const formattedTime = time({
    date,
    formatTime,
    formatMessage,
    timeZone,
    messages,
    hideTimeZone,
    userLocale,
    useEntityTimeFormat,
    entityUse24HourTimeFormat,
  });

  return <span data-testid={testId}>{formattedTime}</span>;
};

export const useTime = ({ overrideTimeZone = undefined, hour12 = undefined, useEntityTimeFormat = false } = {}) => {
  const { formatTime, formatMessage } = useIntl();
  const { timeZone, messages, userLocale } = useLocalizationContext();
  const { entitySetup } = useEntityContext();
  const entityUse24HourTimeFormat = entitySetup?.dateTimeSettings?.use24HourTimeFormat;

  const getTime = React.useCallback(
    (date, hideTimeZone = false) => {
      return time({
        date,
        formatTime,
        formatMessage,
        timeZone: overrideTimeZone ?? timeZone,
        messages,
        hideTimeZone,
        userLocale,
        hour12,
        useEntityTimeFormat,
        entityUse24HourTimeFormat,
      });
    },
    [
      formatMessage,
      formatTime,
      messages,
      timeZone,
      overrideTimeZone,
      userLocale,
      hour12,
      useEntityTimeFormat,
      entityUse24HourTimeFormat,
    ]
  );

  return { getTime, Time };
};
